// src/pages/SubscriptionPage.js
import React, { useEffect, useState, useCallback } from 'react';
import './SubscriptionPage.css';

const fetchJSON = async (url, opts = {}) => {
  const r = await fetch(url, opts);
  const ct = r.headers.get('content-type') || '';
  const body = ct.includes('json') ? await r.json() : await r.text();
  if (!r.ok) throw new Error(body?.detail || body?.message || String(body));
  return body;
};

export default function SubscriptionPage() {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [err, setErr] = useState('');
  const [cancelEmail, setCancelEmail] = useState('');

  const token =
    (typeof localStorage !== 'undefined' &&
      (localStorage.getItem('clairifi_token') || localStorage.getItem('token'))) ||
    '';

  const loadMe = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      if (!token) {
        setMe(null);
      } else {
        const data = await fetchJSON('/api/auth/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMe(data);
      }
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const t = p.get('token');
    if (t) {
      localStorage.setItem('clairifi_token', t);
      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url.toString());
    }
    loadMe();
  }, [loadMe]);

  const upgrade = async () => {
    try {
      setErr('');
      const { url } = await fetchJSON('/api/billing/checkout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = url;
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  const openPortal = async () => {
    try {
      setErr('');
      const { url } = await fetchJSON('/api/billing/portal', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = url;
    } catch (e) {
      setErr(String(e.message || e));
    }
  };
  const adminCancel = async (immediate = false) => {
    try {
      setErr('');
      if (!cancelEmail || !cancelEmail.includes('@')) {
        throw new Error('Enter a valid customer email');
      }
      const qp = immediate ? 'false' : 'true'; // at_period_end
      const { ok } = await fetchJSON(
        `/api/billing/admin/cancel?email=${encodeURIComponent(cancelEmail)}&at_period_end=${qp}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (ok) alert('Cancellation queued.');
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  const signedOut = !token;
  const isCore = me?.plan === 'core';
  const isAdmin = !!me?.is_admin;
  const uploadsUsed = me?.uploads_used ?? 0;

  const uploadsLine = isAdmin ? (
    <li><strong>Unlimited uploads</strong></li>
  ) : (
    <li><strong>3 uploads</strong> per month</li>
  );

  return (
    <div className="sub-wrap">
      <header className="sub-header">
        <div className="sub-eyebrow">PLANS &amp; PRICING</div>
        <h1>Choose your <span className="brand">Clairifi</span> plan</h1>
        <p className="muted">
          Full-stack finance, beautiful insights. Upgrade to <strong>Clairifi Core</strong> for complete access.
        </p>
      </header>

      {err && <div className="sub-alert">{err}</div>}

      {loading ? (
        <div className="sub-loading">Loading…</div>
      ) : (
        <>
          <div className="sub-grid" style={{ marginBottom: 48 }}>
          {/* Free Trial */}
          <section className={`sub-card ${!isCore ? 'sub-card-active' : ''}`}>
            <h2>Free Trial</h2>
            <div className="price">
              <span className="price-int">$0</span>
              <span className="per">/month</span>
            </div>
            <ul className="feat">
              <li>14-day access</li>
              <li>Dashboard, Strategy, Forecast</li>
              <li><strong>1 upload</strong> during trial</li>
            </ul>

            {/* bottom-aligned actions */}
            <div className="actions">
              {signedOut ? (
                <a className="btn btn-primary" href="/api/auth/login/google">
                  Continue with Google
                </a>
              ) : (
                !isCore ? (
                  <a className="btn btn-primary" href="/account">
                    Manage subscription
                  </a>
                ) : (
                  <button className="btn btn-primary btn-disabled" disabled>
                    You’re on Core
                  </button>
                )
              )}
            </div>
          </section>

          {/* Core */}
          <section className={`sub-card sub-card-core ${isCore ? 'sub-card-active' : ''}`}>
            <div className="ribbon">MOST POPULAR</div>
            <h2>Clairifi Core</h2>
            <div className="price">
              <span className="price-int">$19</span>
              <span className="price-dec">.99</span>
              <span className="per">/month</span>
            </div>
            <ul className="feat">
              <li><strong>Full access</strong> to all tools</li>
              {uploadsLine}
              <li>Priority support</li>
              <li>Cancel anytime</li>
            </ul>

            {/* bottom-aligned actions */}
            <div className="actions">
              {signedOut && (
                <a className="btn btn-primary" href="/api/auth/login/google">
                  Sign in to upgrade
                </a>
              )}

              {!signedOut && !isCore && (
                <button className="btn btn-primary" onClick={upgrade}>
                  Upgrade to Core — $19.99/mo
                </button>
              )}

              {!signedOut && isCore && (
                <>
                  <div className="quota">
                    {isAdmin ? (
                      <>Uploads this month: <strong>Unlimited</strong></>
                    ) : (
                      <>Uploads used this month: <strong>{uploadsUsed} / 3</strong></>
                    )}
                  </div>
                  <a className="btn btn-primary" href="/account">
                    Manage subscription
                  </a>
                </>
              )}
            </div>
          </section>
        </div>
        {isAdmin && (
          <section id="admin-billing-tools" className="sub-card" style={{ marginTop: 40 }}>
            <h3>Admin billing tools</h3>
            <p className="muted">Cancel a customer’s subscription without leaving Clairifi.</p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="email"
                placeholder="Customer email"
                value={cancelEmail}
                onChange={(e) => setCancelEmail(e.target.value)}
                style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', minWidth: 280 }}
              />
              <button className="btn btn-primary" onClick={() => adminCancel(false)}>
                Cancel now
              </button>
              <button className="btn" onClick={() => adminCancel(true)}>
                Cancel at period end
              </button>
            </div>
          </section>
        )}
      </>
    )}
  </div>
);
}
